import React from 'react';
import { SlaTracker } from '@/types/domain/nfa-lfa';
import { Card, CardContent } from '@/components/ui/Card';
import { Clock, AlertTriangle, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SlaTrackerWidgetProps {
  sla: SlaTracker;
  className?: string;
}

export const SlaTrackerWidget: React.FC<SlaTrackerWidgetProps> = ({ sla, className }) => {
  const isOverdue = sla.status === 'Overdue';
  const isEscalated = sla.status === 'Escalated';
  const isDueSoon = sla.status === 'DueSoon';

  return (
    <Card
      className={cn(
        'border transition-all shadow-2xs',
        isOverdue && 'border-rose-500/30 bg-rose-500/5',
        isEscalated && 'border-purple-500/30 bg-purple-500/5',
        isDueSoon && 'border-amber-500/30 bg-amber-500/5',
        !isOverdue && !isEscalated && !isDueSoon && 'border-border bg-card',
        className
      )}
    >
      <CardContent className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-lg p-2',
              isOverdue && 'bg-rose-500/20 text-rose-600 dark:text-rose-400',
              isEscalated && 'bg-purple-500/20 text-purple-600 dark:text-purple-400',
              isDueSoon && 'bg-amber-500/20 text-amber-600 dark:text-amber-400',
              !isOverdue && !isEscalated && !isDueSoon && 'bg-primary/10 text-primary'
            )}
          >
            {isEscalated ? (
              <ShieldAlert className="h-5 w-5 animate-pulse" />
            ) : isOverdue ? (
              <AlertTriangle className="h-5 w-5 animate-bounce" />
            ) : (
              <Clock className="h-5 w-5" />
            )}
          </div>

          <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-secondary">
              Approval SLA Tracker ({sla.slaHours}h Target)
            </span>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="font-mono text-sm font-bold text-foreground">
                {sla.remainingMinutes > 0
                  ? `${Math.floor(sla.remainingMinutes / 60)}h ${sla.remainingMinutes % 60}m remaining`
                  : 'SLA Elapsed'}
              </span>
              <span
                className={cn(
                  'rounded px-1.5 py-0.5 text-[9px] font-bold uppercase border',
                  isEscalated && 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/30',
                  isOverdue && 'bg-rose-500/20 text-rose-700 dark:text-rose-300 border-rose-500/30',
                  isDueSoon && 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30',
                  !isOverdue && !isEscalated && !isDueSoon && 'bg-primary/10 text-primary border-primary/20'
                )}
              >
                {sla.status}
              </span>
            </div>
          </div>
        </div>

        <div className="text-right hidden sm:block">
          <span className="text-[10px] text-foreground-muted block">Target SLA Deadline:</span>
          <span className="text-xs font-semibold text-foreground">
            {new Date(sla.targetTimestamp).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
